import styled, { keyframes, css } from 'styled-components';
import colors from '../../../../../constants/colors';
import Link from '../../../../Link';

const BRAND_GRADIENT = `linear-gradient(90deg, ${colors.main.ruby} 0%, ${colors.main.blue} 100%)`;

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fade = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const entrance = (animation) => css`
  opacity: 0;
  animation: ${animation} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const Intro = styled.section`
  margin: 0 auto 40px;
  max-width: 820px;
  text-align: center;

  ${(props) =>
    props.$hasLibrary &&
    css`
      border-bottom: 1px solid ${colors.main.dividers};
      padding-bottom: 40px;
    `}

  @media screen and (max-width: 550px) {
    margin-bottom: 30px;

    ${(props) =>
      props.$hasLibrary &&
      css`
        padding-bottom: 30px;
      `}
  }
`;

const Lead = styled.p`
  color: ${colors.main.grey};
  font-size: 16px;
  letter-spacing: 0.02em;
  line-height: 26px;
  margin: 0 auto;
  max-width: 580px;
  ${entrance(rise)};
`;

const Progression = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  justify-content: center;
  margin: 24px 0;

  & > *:nth-child(1) {
    animation-delay: 0.08s;
  }
  & > *:nth-child(2) {
    animation-delay: 0.18s;
  }
  & > *:nth-child(3) {
    animation-delay: 0.28s;
  }
  & > *:nth-child(4) {
    animation-delay: 0.38s;
  }
  & > *:nth-child(5) {
    animation-delay: 0.48s;
  }

  @media screen and (max-width: 550px) {
    flex-direction: column;
    gap: 4px;
    margin: 20px 0;
  }
`;

const Phase = styled.span`
  color: ${colors.main.black};
  font-family: 'DINNextW01-Condensed', Arial, sans-serif;
  font-size: clamp(28px, 5vw, 46px);
  font-weight: 300;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
  ${entrance(rise)};
`;

const Arrow = styled.span`
  background: ${BRAND_GRADIENT};
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  font-family: 'DINNextW01-Condensed', Arial, sans-serif;
  font-size: clamp(22px, 4vw, 34px);
  line-height: 1;
  ${entrance(fade)};

  @media screen and (max-width: 550px) {
    transform: rotate(90deg);
  }
`;

const Closing = styled.p`
  color: ${colors.main.grey};
  font-size: 16px;
  letter-spacing: 0.02em;
  line-height: 26px;
  margin: 0 auto;
  max-width: 580px;
  ${entrance(rise)};
  animation-delay: 0.56s;
`;

const Emphasis = styled.span`
  color: ${colors.main.black};
  font-weight: 500;
`;

const linkEmphasis = css`
  color: ${colors.main.black};
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.main.ruby};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const EmphasisLink = styled(Link)`
  ${linkEmphasis};
`;

const SupplementalLink = styled.a`
  ${linkEmphasis};
`;

export {
  Intro,
  Lead,
  Progression,
  Phase,
  Arrow,
  Closing,
  Emphasis,
  EmphasisLink,
  SupplementalLink,
};
